import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { Modal, ConfirmModal } from "@/components/application/modals/modal-controlled";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { ApiError, apiCall } from "../../lib/apiClient";
import { renderConsentMarkdown } from "../../lib/markdownRender";

const DEFAULT_BODY_TEMPLATE = `## Souhlas se zpracováním osobních údajů

Tímto souhlasem dáváte správci **Expertní služby s.r.o.** možnost zpracovávat
vaše osobní údaje pro účely rozpočítávání spotřeby tankovacích karet a
doručování úhradových QR kódů.

### Rozsah údajů

- Jméno, e-mail
- SPZ vozidla
- Záznamy o tankování (datum, množství, částka)
- Záznamy o úhradách (datum, částka, variabilní symbol)

### Doba uchování

10 let (zákonná lhůta pro daňové doklady).

### Vaše práva

Máte právo na přístup, opravu, výmaz, omezení zpracování a přenositelnost
údajů podle GDPR čl. 15–20.
`;

/**
 * Consent text versions (DEC-013 Policy C hybrid).
 *
 * GlobalAdmin spravuje chain verzí consent textu. Publikace nové verze:
 *   - `cosmetic` → silent re-accept (stará verze platí pro existující users)
 *   - `material` → re-prompt při příštím loginu (Phase 6 ConsentReprompMiddleware)
 *
 * Backend: ConsentVersionRoutes (GET list/latest, POST publish).
 */

type ChangeType = "material" | "cosmetic";

interface ConsentVersionDto {
  version: string;
  previous_version: string | null;
  change_type: string;
  change_summary: string;
  re_prompt_triggered: boolean;
  introduced_at: string;
  body_markdown: string;
}

export function ConsentVersionsPage() {
  const [rows, setRows] = useState<ConsentVersionDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [publishOpen, setPublishOpen] = useState<boolean>(false);
  const [version, setVersion] = useState<string>("");
  const [changeType, setChangeType] = useState<ChangeType>("cosmetic");
  const [changeSummary, setChangeSummary] = useState<string>("");
  const [previousVersionOverride, setPreviousVersionOverride] = useState<string>("");
  const [bodyMarkdown, setBodyMarkdown] = useState<string>("");
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  // Vnořené potvrzení materiální změny (in-app, ne native window.confirm).
  const [confirmMaterialOpen, setConfirmMaterialOpen] = useState<boolean>(false);

  // Per-row "Zobrazit text" action.
  const [textViewer, setTextViewer] = useState<ConsentVersionDto | null>(null);

  const latest = rows[0] ?? null;

  const fetchList = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await apiCall<ConsentVersionDto[]>("/api/admin/consent-versions");
      // Backend vrací chain — nejnovější chceme nahoře.
      const sorted = [...list].sort((a, b) =>
        b.introduced_at.localeCompare(a.introduced_at),
      );
      setRows(sorted);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Načtení chain verzí selhalo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const openPublish = useCallback(() => {
    // Default: dnešní datum jako version slug, předchozí = latest.version, cosmetic.
    const today = new Date().toISOString().slice(0, 10);
    setVersion(today);
    setChangeType("cosmetic");
    setChangeSummary("");
    setPreviousVersionOverride(latest?.version ?? "");
    // Předvyplň body z latest jako baseline pro editaci (snadnější CLARIFY/cosmetic
    // úpravy). Pokud chain je prázdný, začni s minimal template.
    setBodyMarkdown(latest?.body_markdown ?? DEFAULT_BODY_TEMPLATE);
    setShowPreview(false);
    setPublishError(null);
    setPublishOpen(true);
  }, [latest]);

  const closePublish = useCallback(() => {
    if (publishing) return;
    setPublishOpen(false);
    setPublishError(null);
  }, [publishing]);

  const doPublish = useCallback(async () => {
    setPublishing(true);
    setPublishError(null);
    try {
      const previousVersion = previousVersionOverride.trim() || null;
      await apiCall<ConsentVersionDto>("/api/admin/consent-versions", {
        method: "POST",
        body: JSON.stringify({
          version: version.trim(),
          change_type: changeType,
          change_summary: changeSummary.trim(),
          previous_version: previousVersion,
          body_markdown: bodyMarkdown,
        }),
      });
      await fetchList();
      setPublishOpen(false);
    } catch (e) {
      if (e instanceof ApiError) {
        setPublishError(mapPublishError(e));
      } else {
        setPublishError(e instanceof Error ? e.message : "Publish selhal");
      }
    } finally {
      setPublishing(false);
    }
  }, [version, changeType, changeSummary, previousVersionOverride, bodyMarkdown, fetchList]);

  const submitPublish = useCallback(() => {
    if (!version.trim() || !changeSummary.trim()) {
      setPublishError("Version i change summary jsou povinné.");
      return;
    }
    if (!bodyMarkdown.trim()) {
      setPublishError("Text souhlasu je povinný — bez něj nelze verzi publikovat.");
      return;
    }
    // Materiální změna → in-app potvrzení (re-prompt všech uživatelů). Cosmetic
    // publikuj rovnou.
    if (changeType === "material") {
      setPublishError(null);
      setConfirmMaterialOpen(true);
      return;
    }
    void doPublish();
  }, [version, changeSummary, bodyMarkdown, changeType, doPublish]);

  const columns = useMemo<Column<ConsentVersionDto>[]>(
    () => [
      {
        key: "version",
        header: "Version",
        cell: (row) => (
          <div>
            <strong>{row.version}</strong>
            {row === latest && (
              <Badge color="blue" className="ml-2">
                latest
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: "previous",
        header: "Předchozí",
        cell: (row) => row.previous_version ?? <span className="text-tertiary">—</span>,
      },
      {
        key: "type",
        header: "Typ změny",
        cell: (row) => (
          <Badge color={row.change_type === "material" ? "warning" : "gray"}>
            {row.change_type}
          </Badge>
        ),
      },
      {
        key: "summary",
        header: "Shrnutí",
        cell: (row) => row.change_summary,
      },
      {
        key: "reprompt",
        header: "Nové odsouhlasení",
        cell: (row) => (
          <Badge color={row.re_prompt_triggered ? "warning" : "success"}>
            {row.re_prompt_triggered ? "ANO" : "ne"}
          </Badge>
        ),
      },
      {
        key: "introduced",
        header: "Publikováno",
        cell: (row) => {
          const at = new Date(row.introduced_at);
          return (
            <div className="text-xs">
              {at.toLocaleString("cs-CZ")}
            </div>
          );
        },
      },
      {
        key: "actions",
        header: <span className="sr-only">Akce</span>,
        cell: (row) => (
          <Button
            size="sm"
            color="secondary"
            onPress={() => setTextViewer(row)}
            isDisabled={!row.body_markdown}
            aria-label={`Zobrazit text verze ${row.version}`}
          >
            {row.body_markdown ? "Zobrazit text" : "Bez textu"}
          </Button>
        ),
      },
    ],
    [latest],
  );

  return (
    <section aria-labelledby="consent-versions-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <h2 id="consent-versions-title" className="text-display-xs font-bold text-primary m-0">
            Verze textu souhlasu
          </h2>
          <p className="text-md text-tertiary m-0 max-w-2xl">
            <strong className="text-primary">Kosmetická</strong> změna platí jen pro nově registrované.{" "}
            <strong className="text-primary">Materiální</strong> změna vyzve všechny existující uživatele
            k novému odsouhlasení při příštím přihlášení.
          </p>
        </div>
        <Button color="primary" onPress={openPublish}>
          Publikovat novou verzi
        </Button>
      </header>

      {loadError && <InlineAlert variant="error">{loadError}</InlineAlert>}

      <Card>
        <CardHeader title="Historie verzí" />
        <CardBody>
          <DataTable
            caption="Chain publish-ed verzí consent textu"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.version}
            loading={loading}
            emptyMessage="Žádná verze zatím nebyla publikována."
          />
        </CardBody>

      {publishOpen && (
        <Modal
          open
          title="Publikovat novou verzi consent textu"
          onClose={closePublish}
          footer={
            <>
              <Button color="secondary" onPress={closePublish} isDisabled={publishing}>
                Zrušit
              </Button>
              <Button
                color="primary"
                onPress={submitPublish}
                isDisabled={publishing}
              >
                {publishing ? "Publikuji…" : "Publikovat"}
              </Button>
            </>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitPublish();
            }}
            aria-live="polite"
          >
            <div className="flex flex-col gap-4">
              <Input
                label="Verze"
                isRequired
                hint="Doporučeno datum publikace (YYYY-MM-DD) nebo semver."
                value={version}
                onChange={setVersion}
                placeholder="2026-05-24"
                isDisabled={publishing}
                autoFocus
              />

              <Input
                label="Předchozí verze"
                hint="Předvyplněno z aktuální nejnovější. Prázdné pro úplně první verzi."
                value={previousVersionOverride}
                onChange={setPreviousVersionOverride}
                placeholder={latest?.version ?? "(žádná)"}
                isDisabled={publishing}
              />

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-secondary">
                  Typ změny<span aria-hidden="true"> *</span>
                </span>
                <select
                  className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
                  value={changeType}
                  onChange={(e) => setChangeType(e.target.value as ChangeType)}
                  disabled={publishing}
                >
                  <option value="cosmetic">
                    Kosmetická — překlepy, formulační úpravy, překlady
                  </option>
                  <option value="material">
                    Materiální — nové údaje, nový zpracovatel, změna práv,
                    doby uchování, správce nebo právního základu
                  </option>
                </select>
                <span className="text-sm text-tertiary">
                  {changeType === "material"
                    ? "Existující uživatelé budou při příštím přihlášení vyzváni k novému odsouhlasení. Do té doby uvidí aplikaci jen pro čtení."
                    : "Platí jen pro nově registrované uživatele. Existující bez akce."}
                </span>
              </label>

              <TextArea
                label="Shrnutí změny"
                isRequired
                value={changeSummary}
                onChange={setChangeSummary}
                rows={3}
                placeholder="Co se změnilo a proč (odkaz na posouzení změny, právní podklad, …)"
                isDisabled={publishing}
              />

              <TextArea
                label="Text souhlasu (markdown)"
                isRequired
                hint="Samotná textace — `## nadpis`, `- seznam`, `**tučně**`, `*kurzíva*`, `[odkaz](https://…)`. Zobrazí se uživateli při udělování souhlasu."
                value={bodyMarkdown}
                onChange={setBodyMarkdown}
                rows={14}
                className="font-mono"
                isDisabled={publishing}
              />

              <div>
                <Button
                  type="button"
                  size="sm"
                  color="secondary"
                  onPress={() => setShowPreview((v) => !v)}
                  isDisabled={publishing}
                  aria-expanded={showPreview}
                  aria-controls="consent-body-preview"
                >
                  {showPreview ? "Skrýt náhled" : "Zobrazit náhled"}
                </Button>
                {showPreview && (
                  <div
                    id="consent-body-preview"
                    className="ring-1 ring-secondary rounded-xl p-4 mt-2 bg-secondary"
                    dangerouslySetInnerHTML={{ __html: renderConsentMarkdown(bodyMarkdown) }}
                  />
                )}
              </div>

              {changeType === "material" && (
                <InlineAlert variant="warning">
                  <strong>Pozor:</strong> Po publikaci budou všichni existující
                  uživatelé při příštím přihlášení vyzváni k novému odsouhlasení.
                </InlineAlert>
              )}

              {publishError && <InlineAlert variant="error">{publishError}</InlineAlert>}
            </div>
          </form>
        </Modal>
      )}

      <ConfirmModal
        open={confirmMaterialOpen}
        title="Publikovat materiální změnu?"
        message="Publikace materiální změny vyzve všechny existující uživatele k novému odsouhlasení při příštím přihlášení."
        confirmLabel="Publikovat"
        cancelLabel="Zpět"
        onConfirm={() => {
          setConfirmMaterialOpen(false);
          void doPublish();
        }}
        onCancel={() => setConfirmMaterialOpen(false)}
      />

      </Card>

      {textViewer && (
        <Modal
          open
          title={`Text consentu — verze ${textViewer.version}`}
          onClose={() => setTextViewer(null)}
          footer={<Button color="secondary" onPress={() => setTextViewer(null)}>Zavřít</Button>}
        >
          <div
            className="max-h-[60vh] overflow-y-auto py-2"
            dangerouslySetInnerHTML={{ __html: renderConsentMarkdown(textViewer.body_markdown) }}
          />
        </Modal>
      )}
    </section>
  );
}

function mapPublishError(e: ApiError): string {
  switch (e.errorCode) {
    case "CONSENT_VERSION_EXISTS":
      return `Verze už existuje: ${e.message}`;
    case "CONSENT_VERSION_NOT_FOUND":
      return `Předchozí verze neexistuje: ${e.message}`;
    case "CONSENT_VERSION_CHAIN_BROKEN":
      return `Konflikt verzí — mezi-tím byla publikována jiná verze. Obnov stránku a zkus znovu.`;
    case "VALIDATION_ERROR":
      return `Validační chyba: ${e.message}`;
    default:
      return e.message || `HTTP ${e.status}`;
  }
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/consent/versions",
  component: ConsentVersionsPage,
});
